import { useState, useEffect } from "react";
import { 
  Container, 
  Paper, 
  Box,
  Typography,
  Grid,
  useTheme,
  alpha,
  Fade,
  Zoom,
  Chip,
  Avatar,
  Button,
  Stack,
  Divider,
  Tooltip,
  IconButton,
  Badge,
  LinearProgress,
  Alert,
  AlertTitle
} from "@mui/material";
import {
  Restaurant as RestaurantIcon,
  Add as AddIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Calculate as CalculateIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  LocalDining as LocalDiningIcon,
  Scale as ScaleIcon,
  Category as CategoryIcon
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

import { ProductFormDialog } from "../../Components/FoodCost/ProductForm";
import { ProductList } from "../../Components/FoodCost/ProductList";
import Product from "../../Services/FoodCost";
import Stock from "../../Services/Stock";
import DailyServings from "../../Services/DailyServing";
import ExcelTableFoodCost from "../../Components/Tables/ExcelFoodCost";

// Helper function to safely parse numbers
const safeParseNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === '') return defaultValue;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const FoodCostPage = ({ sidebarOpen }) => {
  const theme = useTheme();
  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [form, setForm] = useState({ 
    name: "", 
    soldPrice: "", 
    category: "", // Add category field
    ingredients: []
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [dailyServings, setDailyServings] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currentServing, setCurrentServing] = useState({ 
    id: null, 
    items: [], 
    total: 0 
  });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalStocks: 0,
    totalServings: 0,
    averageCost: 0,
    totalRevenue: 0
  });
  const [isFormOpen, setIsFormOpen] = useState(false);

  const restaurantId = localStorage.getItem("restaurantId");

  // Responsive padding based on sidebar
  const mainContentPadding = {
    pl: sidebarOpen ? (window.innerWidth < 600 ? 1 : 32) : 2,
    pr: 2,
    transition: theme.transitions.create(['padding'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    })
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchProducts(),
        fetchStocks(),
        fetchDailyServings()
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductSubmit = async (formData) => {
    try {
      const validIngredients = formData.ingredients.map(ingredient => ({
        ...ingredient,
        quantity: safeParseNumber(ingredient.quantity),
        costPrice: safeParseNumber(ingredient.costPrice)
      }));

      const productData = {
        ...formData,
        soldPrice: safeParseNumber(formData.soldPrice),
        category: formData.category || "", // Include category
        ingredients: validIngredients
      };

      const product = new Product(
        productData.name,
        productData.soldPrice,
        restaurantId,
        "",
        productData.ingredients,
        productData.category // Pass category to constructor
      );

      if (editingProduct) {
        await product.updateProduct(editingProduct, productData);
      } else {
        await product.addProduct();
      }

      await fetchProducts();
      setForm({ name: "", soldPrice: "", category: "", ingredients: [] });
      setEditingProduct(null);
      setIsFormOpen(false);
    } catch (error) {
      console.error("Error saving product:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const data = await Product.getAllProducts();
      const filteredData = data.filter(p => p.restaurantId === restaurantId);
      
      // Format products with proper number types
      const formattedProducts = filteredData.map(product => {
        // Calculate cost price from ingredients if not already set
        let costPrice = safeParseNumber(product.costPrice);
        
        if ((!costPrice || costPrice === 0) && product.ingredients && product.ingredients.length > 0) {
          costPrice = product.ingredients.reduce((sum, ingredient) => {
            const quantity = safeParseNumber(ingredient.quantity);
            const ingredientCost = safeParseNumber(ingredient.costPrice);
            return sum + (quantity * ingredientCost);
          }, 0);
        }
        
        return {
          ...product,
          id: product.id,
          name: product.name || '',
          category: product.category || '', // Include category
          soldPrice: safeParseNumber(product.soldPrice),
          costPrice: costPrice,
          ingredients: (product.ingredients || []).map(ingredient => ({
            ...ingredient,
            stockName: ingredient.stockName || ingredient.name || '',
            quantity: safeParseNumber(ingredient.quantity),
            costPrice: safeParseNumber(ingredient.costPrice)
          }))
        };
      });
      
      setProducts(formattedProducts);
      
      // Calculate product stats
      const totalCost = formattedProducts.reduce((sum, p) => {
        return sum + (p.costPrice || 0);
      }, 0);
      
      setStats(prev => ({
        ...prev,
        totalProducts: formattedProducts.length,
        averageCost: formattedProducts.length ? totalCost / formattedProducts.length : 0
      }));
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
    }
  };

  const fetchStocks = async () => {
    try {
      const data = await Stock.getAllStocks(restaurantId);
      const formattedStocks = (data || []).map(stock => ({
        ...stock,
        id: stock.id,
        productName: stock.productName || stock.name || '',
        quantity: safeParseNumber(stock.quantity),
        costPrice: safeParseNumber(stock.costPrice),
        sellingPrice: safeParseNumber(stock.sellingPrice)
      }));
      setStocks(formattedStocks);
      setStats(prev => ({ ...prev, totalStocks: formattedStocks.length }));
    } catch (error) {
      console.error("Error fetching stocks:", error);
      setStocks([]);
    }
  };

  const fetchDailyServings = async () => {
    try {
      const data = await DailyServings.getDailyServingsByRestaurantId(restaurantId);
      const normalizedData = (data || []).map(serving => ({
        id: serving.id,
        items: (serving.items || []).map(item => ({
          ...item,
          productId: item.productId,
          productName: item.productName || '',
          quantity: safeParseNumber(item.quantity),
          costPrice: safeParseNumber(item.costPrice),
          soldPrice: safeParseNumber(item.soldPrice)
        })),
        totalCost: safeParseNumber(serving.totalCost),
        realCost: safeParseNumber(serving.realCost),
        date: serving.date || new Date().toISOString(),
        restaurantId: serving.restaurantId
      }));
      setDailyServings(normalizedData);
      
      // Calculate serving stats
      const totalRevenue = normalizedData.reduce((sum, s) => sum + (s.realCost || 0), 0);
      setStats(prev => ({
        ...prev,
        totalServings: normalizedData.length,
        totalRevenue
      }));
    } catch (error) {
      console.error("Error fetching daily servings:", error);
      setDailyServings([]);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product.id);
    setForm({
      name: product.name,
      soldPrice: product.soldPrice.toString(),
      category: product.category || "", // Include category
      ingredients: (product.ingredients || []).map(ing => ({
        stockName: ing.stockName,
        quantity: ing.quantity.toString(),
        costPrice: ing.costPrice?.toString() || ''
      }))
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await new Product().deleteProduct(id);
      await fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...form.ingredients];
    
    if (field === 'stockName') {
      const selectedStock = stocks.find(s => s.productName === value);
      if (selectedStock) {
        newIngredients[index] = {
          ...newIngredients[index],
          stockName: value,
          costPrice: selectedStock.costPrice?.toString() || '0'
        };
      } else {
        newIngredients[index] = {
          ...newIngredients[index],
          [field]: value
        };
      }
    } else {
      newIngredients[index][field] = value;
    }
    
    setForm({ ...form, ingredients: newIngredients });
  };

  const addIngredient = () => {
    setForm({ 
      ...form, 
      ingredients: [...form.ingredients, { 
        stockName: "", 
        quantity: "0",
        costPrice: "0"
      }] 
    });
  };

  const removeIngredient = (index) => {
    const newIngredients = form.ingredients.filter((_, i) => i !== index);
    setForm({ ...form, ingredients: newIngredients });
  };

  const handleProductSelect = (product) => {
    if (!selectedProducts.some(p => p.id === product.id)) {
      const updatedProducts = [...selectedProducts, { 
        ...product, 
        quantity: 1,
        costPrice: product.costPrice || 0,
        soldPrice: product.soldPrice || 0
      }];
      setSelectedProducts(updatedProducts);
      updateTotal(updatedProducts);
    }
  };

  const updateQuantity = (id, quantity) => {
    const updated = selectedProducts.map(p => 
      p.id === id ? { ...p, quantity: Math.max(1, quantity) } : p
    );
    setSelectedProducts(updated);
    updateTotal(updated);
  };

  const updateTotal = (items) => {
    const total = items.reduce((sum, item) => sum + (safeParseNumber(item.costPrice) * safeParseNumber(item.quantity)), 0);
    setCurrentServing(prev => ({ ...prev, items, total }));
  };

  const removeProduct = (id) => {
    const filtered = selectedProducts.filter(p => p.id !== id);
    setSelectedProducts(filtered);
    updateTotal(filtered);
  };

  const addDailyServing = async () => {
    try {
      if (selectedProducts.length === 0) {
        alert("Please select at least one product");
        return;
      }

      const items = selectedProducts.map(p => ({
        productId: p.id,
        productName: p.name,
        quantity: safeParseNumber(p.quantity),
        costPrice: safeParseNumber(p.costPrice),
        soldPrice: safeParseNumber(p.soldPrice)
      }));

      const totalCost = items.reduce((sum, item) => sum + (item.costPrice * item.quantity), 0);
      const realCost = items.reduce((sum, item) => sum + (item.soldPrice * item.quantity), 0);
      const date = new Date().toISOString();

      const newServing = new DailyServings(
        items,
        totalCost,
        realCost,
        date,
        restaurantId
      );

      await newServing.addDailyServing();
      await fetchDailyServings();
      resetServingForm();
    } catch (error) {
      console.error("Error saving daily serving:", error);
    }
  };

  const editDailyServing = (serving) => {
    setCurrentServing({
      id: serving.id,
      items: serving.items,
      totalCost: serving.totalCost,
      realCost: serving.realCost
    });
    
    setSelectedProducts(serving.items.map(item => ({
      id: item.productId,
      name: item.productName,
      quantity: item.quantity,
      costPrice: item.costPrice,
      soldPrice: item.soldPrice
    })));
  };

  const deleteDailyServing = async (id) => {
    try {
      await DailyServings.deleteDailyServing(id);
      setDailyServings(dailyServings.filter(s => s.id !== id));
    } catch (error) {
      console.error("Error deleting daily serving:", error);
    }
  };

  const resetServingForm = () => {
    setCurrentServing({ id: null, items: [], total: 0 });
    setSelectedProducts([]);
  };

  const formatNumber = (num) => {
    const safeNum = safeParseNumber(num);
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(safeNum);
  };

  const handleOpenForm = () => {
    setEditingProduct(null);
    setForm({ name: "", soldPrice: "", category: "", ingredients: [] });
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingProduct(null);
    setForm({ name: "", soldPrice: "", category: "", ingredients: [] });
  };

  return (
    <Box sx={{ 
      ...mainContentPadding,
      pt: 3,
      minHeight: '100vh',
      position: 'relative',
      overflowX: 'hidden'
    }}>
      {/* Background Decoration */}
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(76,175,80,0.03) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          {/* Hero Section */}
          <motion.div variants={fadeInUp}>
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
                borderRadius: 4,
                border: '1px solid rgba(255,255,255,0.1)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)',
                  transition: 'left 0.6s',
                },
                '&:hover::before': {
                  left: '100%'
                }
              }}
            >
              <Box sx={{ p: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                          boxShadow: '0 10px 20px rgba(255,215,0,0.3)',
                        }}
                      >
                        <RestaurantIcon sx={{ fontSize: 48, color: '#1E2A3A' }} />
                      </Avatar>
                    </Zoom>
                    <Box>
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 800,
                          background: 'linear-gradient(45deg, #FFFFFF 30%, #FFD700 90%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 1,
                        }}
                      >
                        Food Cost Management
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 400 }}>
                        Optimisez vos coûts et maximisez vos profits
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Rafraîchir" arrow>
                      <IconButton 
                        onClick={fetchAllData}
                        sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Télécharger" arrow>
                      <IconButton sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Imprimer" arrow>
                      <IconButton sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                        <PrintIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </motion.div>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '40%',
                      height: '100%',
                      background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 100%)',
                      transform: 'skewX(-20deg) translateX(20%)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                      <RestaurantIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Total Produits</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.totalProducts}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '40%',
                      height: '100%',
                      background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 100%)',
                      transform: 'skewX(-20deg) translateX(20%)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                      <InventoryIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Matières Premières</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.totalStocks}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '40%',
                      height: '100%',
                      background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 100%)',
                      transform: 'skewX(-20deg) translateX(20%)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                      <ReceiptIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Ventes du Jour</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.totalServings}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '40%',
                      height: '100%',
                      background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 100%)',
                      transform: 'skewX(-20deg) translateX(20%)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                      <AttachMoneyIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Chiffre d'Affaires</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {formatNumber(stats.totalRevenue)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <motion.div variants={fadeInUp}>
            <Paper sx={{ 
              p: 3, 
              mb: 4, 
              borderRadius: 3,
              background: '#FFFFFF',
              border: '1px solid #E0E0E0'
            }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalculateIcon sx={{ color: '#4CAF50' }} />
                Actions Rapides
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenForm}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(45deg, #667eea, #764ba2)',
                      borderRadius: 2,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 10px 20px rgba(102,126,234,0.3)'
                      }
                    }}
                  >
                    Nouveau Produit
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => window.location.href = '/DailyServings'}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(45deg, #4CAF50, #45a049)',
                      borderRadius: 2,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 10px 20px rgba(76,175,80,0.3)'
                      }
                    }}
                  >
                    Nouvelle Vente
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ScaleIcon />}
                    onClick={() => window.location.href = '/stock'}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      borderColor: '#4CAF50',
                      color: '#4CAF50',
                      '&:hover': {
                        borderColor: '#45a049',
                        bgcolor: alpha('#4CAF50', 0.1)
                      }
                    }}
                  >
                    Gérer Stock
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </motion.div>

          {/* Excel Table Section */}
          <motion.div variants={fadeInUp}>
            <Paper sx={{ 
              mb: 4, 
              borderRadius: 3,
              background: '#FFFFFF',
              border: '1px solid #E0E0E0',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 3, 
                background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)',
                borderBottom: '1px solid #E0E0E0'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon sx={{ color: '#4CAF50' }} />
                    Analyse des Coûts
                  </Typography>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${products.length} produits analysés`}
                    size="small"
                    sx={{ bgcolor: alpha('#4CAF50', 0.1), color: '#4CAF50' }}
                  />
                </Box>
              </Box>
              <Box sx={{ p: 3 }}>
                <ExcelTableFoodCost data={products} />
              </Box>
            </Paper>
          </motion.div>

          {/* Products Section */}
          <motion.div variants={fadeInUp}>
            <Paper sx={{ 
              borderRadius: 3,
              background: '#FFFFFF',
              border: '1px solid #E0E0E0',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 3, 
                background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)',
                borderBottom: '1px solid #E0E0E0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2
              }}>
                <Box>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalDiningIcon sx={{ color: '#4CAF50' }} />
                    Catalogue des Produits
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                    Gérez vos recettes et leurs coûts de revient
                  </Typography>
                </Box>
                <Badge badgeContent={products.length} color="success">
                  <Avatar sx={{ bgcolor: alpha('#4CAF50', 0.1), color: '#4CAF50' }}>
                    <RestaurantIcon />
                  </Avatar>
                </Badge>
              </Box>

              <Box sx={{ p: 3 }}>
                {loading ? (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress sx={{ borderRadius: 1 }} />
                  </Box>
                ) : (
                  <ProductList
                    products={products}
                    handleEdit={handleEdit}
                    handleDelete={handleDelete}
                    onAddNew={handleOpenForm}
                    onProductSelect={handleProductSelect}
                  />
                )}
              </Box>
            </Paper>
          </motion.div>

          {/* Footer Stats */}
          <motion.div variants={fadeInUp}>
            <Box sx={{ 
              mt: 4, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}>
              <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
                Dernière mise à jour: {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  icon={<AttachMoneyIcon />}
                  label={`Coût moyen: ${formatNumber(stats.averageCost)}`}
                  size="small"
                  sx={{ bgcolor: '#F5F7FA', color: '#5F6B7A' }}
                />
                <Chip
                  icon={<TrendingUpIcon />}
                  label={`Marge: ${stats.totalRevenue ? ((stats.totalRevenue - (stats.averageCost * stats.totalProducts)) / stats.totalRevenue * 100).toFixed(1) : 0}%`}
                  size="small"
                  sx={{ bgcolor: '#F5F7FA', color: '#5F6B7A' }}
                />
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      </Container>

      {/* Product Form Dialog */}
      <ProductFormDialog
        initialFormData={form}
        stocks={stocks}
        onSubmit={handleProductSubmit}
        editingProduct={editingProduct}
        handleInputChange={handleInputChange}
        handleIngredientChange={handleIngredientChange}
        addIngredient={addIngredient}
        removeIngredient={removeIngredient}
        open={isFormOpen}
        onClose={handleCloseForm}
      />
    </Box>
  );
};

export default FoodCostPage;